import { z } from "zod";

/** Kind of rate card within a supplier account. */
export const rateCardKind = z.enum(["main", "customer_developer", "supplier_brand"]);
export type RateCardKind = z.infer<typeof rateCardKind>;

/** How a price list arrived. `scrape` is reserved for the future scraping slot. */
export const priceListSourceType = z.enum(["xlsx", "csv", "pdf", "pasted", "scrape"]);
export type PriceListSourceType = z.infer<typeof priceListSourceType>;

/** Price list ingest lifecycle. */
export const priceListStatus = z.enum(["draft", "parsing", "parsed", "published", "failed"]);
export type PriceListStatus = z.infer<typeof priceListStatus>;

/** Provenance of an individual SCD2 price row. */
export const priceSource = z.enum(["upload", "manual_amend", "scrape"]);
export type PriceSource = z.infer<typeof priceSource>;

/** Coarse product domain used for comparables blocking. */
export const productDomain = z.enum([
  "RADIATOR",
  "PUSHFIT",
  "COMPRESSION",
  "TRAP",
  "DRAINAGE",
  "OTHER",
]);
export type ProductDomain = z.infer<typeof productDomain>;

/**
 * Canonical product type — the 19 types from the `build_comparables.py`
 * TYPE_RULES (priority-ordered), plus OTHER, plus the radiator panel types
 * (for RADIATOR-domain products the classifier uses the panel type as the
 * canonical type, mirroring the Python: K1/K2/K3/P+/P1, "K?" when dimensions
 * parsed but the panel type did not).
 */
export const canonicalType = z.enum([
  "K1",
  "K2",
  "K3",
  "P+",
  "P1",
  "K?",
  "PAN_CONNECTOR",
  "TRAP",
  "AAV",
  "WALL_PLATE",
  "TANK_CONN",
  "MANIFOLD",
  "TEE_BRANCH",
  "ELBOW",
  "BEND",
  "REDUCER",
  "COUPLER",
  "STOP_END",
  "SHOE",
  "GUTTER",
  "DOWNPIPE",
  "CLIP_BRACKET",
  "ADAPTOR",
  "VALVE",
  "PIPE",
  "OTHER",
]);
export type CanonicalType = z.infer<typeof canonicalType>;

/** Banded confidence (classifier bands: High >= 82, Medium >= 68). */
export const confidence = z.enum(["high", "medium", "low"]);
export type Confidence = z.infer<typeof confidence>;

/** Human review state on comparable-group memberships. */
export const reviewState = z.enum(["pending", "approved", "rejected"]);
export type ReviewState = z.infer<typeof reviewState>;

/** Match state of a pricing line item. */
export const matchState = z.enum([
  "unmatched",
  "needs_review",
  "auto_matched",
  "confirmed",
  "rejected",
]);
export type MatchState = z.infer<typeof matchState>;

/** Where a product classification came from (override = human correction). */
export const classificationSource = z.enum(["deterministic", "override", "ai"]);
export type ClassificationSource = z.infer<typeof classificationSource>;

/** Pricing run lifecycle. */
export const pricingStatus = z.enum(["draft", "matching", "ready", "finalised", "archived"]);
export type PricingStatus = z.infer<typeof pricingStatus>;

/** Pricing strategy mode: whole batch to one supplier vs cheapest per line. */
export const pricingMode = z.enum(["single_supplier", "cheapest_per_line"]);
export type PricingMode = z.infer<typeof pricingMode>;

/** Project lifecycle. */
export const projectStatus = z.enum(["active", "archived"]);
export type ProjectStatus = z.infer<typeof projectStatus>;

/** Kind of uploaded file. */
export const uploadKind = z.enum(["price_list", "shopping_list"]);
export type UploadKind = z.infer<typeof uploadKind>;

/** Kind of logged AI job. */
export const aiJobKind = z.enum([
  "parse_price_list",
  "parse_buy_list",
  "classify_products",
  "build_comparables",
  "match_shopping_list",
]);
export type AiJobKind = z.infer<typeof aiJobKind>;

/** AI job lifecycle. */
export const aiJobStatus = z.enum(["queued", "running", "succeeded", "failed"]);
export type AiJobStatus = z.infer<typeof aiJobStatus>;
